import { BaseViewStore, HyperlinkCellData } from 'core/stores';
import { action, observable, makeObservable, computed } from 'mobx';
import { PalletCreateForm, PalletizeFulfillmentOrderForm } from 'modules/order/forms';
import { PalletStore, FulfillmentOrderStore } from 'modules/order/stores';
import { ShipmentInfoStore } from 'modules/shipment-info/stores';
import { ApplicationStore } from 'platform/application/stores';
import {
	formInvalidate,
	notificationError,
	isSeller,
	isSuperAdminOrganization,
	isWarehouse,
	createFileUrl,
} from 'core/utils';
import { lookupRouteName, shipmentInfoTypeEnum, sourceOfChange } from 'common/constants';
import { SoundService } from 'modules/sound/services';
import moment from 'moment';
import _ from 'lodash';
import { BaasicDropdownViewStore } from 'common/stores';
import { DeletePalletDropdownAction } from 'core/components/select';
import { LookupStore } from 'modules/lookup/stores';

class PalletizingModeViewStore extends BaseViewStore {
	@observable data = [];
	@observable barcode = null;
	@observable createPalletNotification = null;
	@observable palletFindNotification = null;
	@observable scanPackageNotification = null;
	@observable undoNotification = null;
	@observable lastAction = null;
	@observable newPallet = false;
	@observable label = null;
	@observable isPalletDropdownLoading = false;
	@observable page = 1;
	@observable infiniteSearchPhrase = null;
	@observable orderCheck = null;
	@observable selectedValue = null;
	@observable selectedLabel = null;
	searchPhrase = null;
	barcodeRef = null;
	trackingNumber = null;
	barcodeInput = null;
	trackingNumberInput = null;
	pallet = null;
	debouncedShipmentInfoSearch = null;
	originalBarcode = null;

	palletCreateForm = new PalletCreateForm({
		onSuccess: this.createPallet,
	});

	palletizingForm = new PalletizeFulfillmentOrderForm();

	constructor(rootStore, { modalParams }) {
		super(rootStore);
		makeObservable(this);
		this.modalParams = modalParams;
		this.shipmentInfoStore = new ShipmentInfoStore(rootStore);
		this.palletStore = new PalletStore(rootStore);
		this.organizationStore = new ApplicationStore(rootStore);
		this.soundService = new SoundService();
		this.fulfillmentOrderStore = new FulfillmentOrderStore(rootStore);
		this.isSeller = isSeller(rootStore);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.isWarehouse = isWarehouse(rootStore);
		this.defineTableData();

		this.initialize();
	}

	@action.bound
	async initialize() {
		this.suspend();
		await this.initializeDropdowns();
		this.resume();
	}

	@action.bound
	initializeDropdowns = async () => {
		this.warehouseDropdownStore = new BaasicDropdownViewStore();
		await this.warehouseDropdownStore.initialize({
			field: this.palletizingForm.$('warehouseId'),
			store: new ApplicationStore(this.rootStore),
			onChangeCallback: value => this.onWarehouseChange(value),
			filterProperties: { warehouseOnly: true, isOrganizationActive: true },
			itemTemplate: {
				value: 'id',
				label: 'applicationTitle',
			},
		});
		this.palletDropdownStore = new BaasicDropdownViewStore();
		await this.palletDropdownStore.initialize({
			field: this.palletizingForm.$('palletId'),
			store: new PalletStore(this.rootStore),
			onChangeCallback: value => this.onPalletChange(value),
			fetchInitialValues: this.isWarehouse,
			itemTemplate: {
				value: 'id',
				label: 'barcode',
			},
			filterCallback: async options => this.palletFilterCallback(options),
			filterProperties: this.palletFilter,
		});
		this.carrierTypeServiceTypeDropdownStore = new BaasicDropdownViewStore();
		await this.carrierTypeServiceTypeDropdownStore.initialize({
			lookupRoute: lookupRouteName.carrierTypeServiceType,
			store: new LookupStore(this.rootStore),
			field: this.palletCreateForm.$('carrierTypeServiceTypeId'),
			onChangeCallback: option => {
				if (option) this.barcode = `${option.extra.code}-${this.originalBarcode}`;
			},
		});
	};

	runAction = async () => {
		await this.externalComponent.reload();
	};

	@action.bound
	palletFilterCallback = async options => {
		if (options.length === 1) {
			this.palletDropdownStore.setValue(options[0]);
			this.palletFindNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.FOUND';
			if (this.palletDropdownStore.reference != null) {
				this.palletDropdownStore.reference.setState({ menuIsOpen: false });
			}
			await this.soundService.success();
		} else if (options.length === 0) {
			const inputRefValue = _.get(this.palletDropdownStore, 'reference.select.inputRef.value');
			if (inputRefValue && inputRefValue !== '') {
				this.palletFindNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.NOTFOUND';
				this.scanPackageNotification = null;
				await this.soundService.error();
			}
			if (this.palletDropdownStore.value) this.palletDropdownStore.clear();
			if (_.get(this.palletDropdownStore, 'reference.select')) {
				this.palletDropdownStore.reference.select.setState({ menuIsOpen: false, inputValue: '' });
				this.palletDropdownStore.reference.blur();
				this.palletDropdownStore.reference.focus();
			}
		}
		return options.map(o => {
			return this.palletDropdownStore.constructOption(o);
		});
	};

	deletePalletOptionOverride = {
		shouldDisplayCallback: value => this.displayDeleteAction(value),
		Component: DeletePalletDropdownAction,
		onClick: option => this.openDeleteConfirmModal(option),
	};

	openDeleteConfirmModal = option => {
		this.openConfirmationModal(this.getDeleteConfirmModalProps(option));
	};

	displayDeleteAction = data => {
		return _.get(data, 'extra.shipmentInfos.length') == 0 && (this.isSuperAdminOrganization || this.isWarehouse);
	};

	@action.bound
	togglePallet() {
		this.data = [];
		this.palletCreateForm.clear();
		this.palletizingForm.$('trackingNumber').clear();
		this.selectedCarrier = null;
		this.palletFindNotification = null;
		this.scanPackageNotification = null;
		this.createPalletNotification = null;
		this.newPallet = !this.newPallet;
		this.undoNotification = null;
		if (this.newPallet) {
			this.palletCreateForm.$('name').value = moment().toString();
			this.generateBarcode();
		}
	}

	@action.bound
	generateBarcode() {
		var result = '';
		const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
		const charactersLength = characters.length;
		for (var i = 0; i < 10; i++) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength));
		}
		this.barcode = result;
		this.originalBarcode = result;
	}

	@action.bound
	addBarcodePrefix(item) {
		if (!item) return;
		let carrierCharacter = item.extra.carrierServiceType.abrv === 'ground' ? 'G' : 'E';
		switch (item.extra.carrierType.abrv) {
			case 'fedex':
				this.barcode = `FDX${carrierCharacter}-${this.originalBarcode}`;
				break;
			case 'ups':
				this.barcode = `UPS${carrierCharacter}-${this.originalBarcode}`;
				break;
			case 'stamps':
				this.barcode = `STAMPS${carrierCharacter}-${this.originalBarcode}`;
				break;
			case 'passport':
				this.barcode = `PASSPORT${carrierCharacter}-${this.originalBarcode}`;
			case 'amazon%20shipping':
				this.barcode = `AMAZON${carrierCharacter}-${this.originalBarcode}`;
		}
	}

	@action.bound
	async onPalletChange(item) {
		this.data = [];
		this.undoNotification = null;
		this.palletFindNotification = null;
		this.scanPackageNotification = null;
		this.createPalletNotification = null;
		this.palletFindNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.FOUND';
		await this.soundService.success();
		if (item && this.trackingNumberInput != null) {
			if (this.trackingNumberInput.disabled) {
				this.trackingNumberInput.disabled = false;
			}
			this.trackingNumberInput.focus();
		}
		if (item) this.transformData(item.extra.shipmentInfos);
	}

	@computed get palletFilter() {
		return {
			isOpened: true,
			isSeller: this.isSeller || this.isSuperAdminOrganization,
			warehouseIds:
				(this.isSeller || this.isSuperAdminOrganization) && this.palletizingForm.$('warehouseId').value
					? [this.palletizingForm.$('warehouseId').value]
					: undefined,
			embed: [
				'shipmentInfos',
				'shipmentInfos.fulfillmentOrder',
				'shipmentInfos.fulfillmentOrder.fulfillmentOrderStatus',
				'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems',
				'shipmentInfos.fulfillmentOrder.fulfillmentOrderItems.product',
				'carrierService',
				'carrierService.carrierServiceType',
				'carrierService.carrierType',
				'carrierTypeServiceType',
			],
		};
	}

	@action.bound
	async onWarehouseChange(item) {
		this.data = [];
		if (this.palletDropdownStore.value) this.palletDropdownStore.clear();
		this.undoNotification = null;
		this.palletFindNotification = null;
		this.scanPackageNotification = null;
		this.createPalletNotification = null;
		if (item != null) {
			await this.palletDropdownStore.updateFilter(this.palletFilter);
		} else {
			this.palletizingForm.$('warehouseId').focus();
		}
	}

	@action.bound
	async createPallet() {
		try {
			if (this.barcodeRef != null) {
				var label = this.barcodeRef.refs.renderElement.currentSrc.split(',')[1];
			}
			const { warehouseId } = this.palletizingForm.values();
			const response = await this.palletStore.create(
				{
					...this.palletCreateForm.values(),
					barcode: this.barcode,
					open: true,
					label,
					warehouseId,
				},
				sourceOfChange.palletizingModeCreatePallet
			);
			if (response.hasOwnProperty('statusCode')) {
				this.rootStore.notificationStore.success('FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PRINTJOBCREATED');
			} else {
				this.rootStore.notificationStore.warning('FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PRINTNODEINFONOTFOUND');
			}
			this.palletCreateForm.$('carrierTypeServiceTypeId').value = '';
			this.newPallet = false;
			this.palletFindNotification = null;
			if (response.hasOwnProperty('pallets')) {
				this.pallet = response.pallets[0];
			} else {
				this.pallet = response[0];
			}
			this.palletDropdownStore.setValue(this.pallet);
			this.undoNotification = null;
			this.createPalletNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.SUCCESS';
			this.pendingLoadingSuspend();
			await this.palletDropdownStore.updateFilter(this.palletFilter);
			this.resume();
			if (this.trackingNumberInput != null) {
				this.trackingNumberInput.focus();
			}
		} catch (err) {
			if (err.statusCode == 409) {
				this.createPalletNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PALLET.CONFLICT';
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
	}

	@action.bound
	async onKeyUp(event) {
		if (event.currentTarget.value.length != 0) {
			const valueWithoutWhitespace = event.currentTarget.value.replace(/ |\t/g, ''); // Remove whitespace and tabs.
			this.palletizingForm.$('trackingNumber').value = valueWithoutWhitespace;
			if (!this.debouncedShipmentInfoSearch) {
				this.debouncedShipmentInfoSearch = _.debounce(() => {
					this.findShipmentInfo();
				}, 500);
			}

			this.debouncedShipmentInfoSearch();
		} else {
			this.scanPackageNotification = null;
		}
	}

	@action.bound
	async findShipmentInfo() {
		try {
			const { trackingNumber, palletId } = this.palletizingForm.values();
			this.undoNotification = null;
			this.trackingNumber =
				trackingNumber.length === 34 ? trackingNumber.substring(trackingNumber.length - 12) : trackingNumber;
			await this.shipmentInfoStore.updateShipmentInfoByTrackingNumber(
				{
					trackingNumber: this.trackingNumber,
					palletId,
				},
				sourceOfChange.palletizingModeUpdateShipmentInfoByTrackingNumber
			);
			await this.shipmentInfoStore.find({
				page: 1,
				rpp: 100,
				isPalletizing: true,
				palletId: palletId,
				shipmentInfoType: shipmentInfoTypeEnum.shipment,
				embed: [
					'fulfillmentOrder',
					'fulfillmentOrder.fulfillmentOrderStatus',
					'fulfillmentOrder.fulfillmentOrderItems',
					'fulfillmentOrder.fulfillmentOrderItems.orderLine',
					'fulfillmentOrder.fulfillmentOrderItems.orderLine.product',
					'fulfillmentOrder.fulfillmentOrderItems.orderLine.product.productVirtualSkus',
					'fulfillmentOrder.fulfillmentOrderItems.product',
				],
			});
			this.data = [];
			this.transformData(this.shipmentInfoStore.data);
			this.scanPackageNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PACKAGESCAN.SUCCESS';
			await this.soundService.success();
			this.lastAction = 'palletize';
			this.undoNotification = null;
			if (this.palletDropdownStore.value != null) {
				this.palletDropdownStore.value.extra.shipmentInfos = this.shipmentInfoStore.data;
			}
			setTimeout(() => {
				if (!this.newPallet) {
					this.palletizingForm.$('trackingNumber').clear();
					if (this.trackingNumberInput != null) {
						this.trackingNumberInput.focus();
					}
				}
			}, 1500);
		} catch (err) {
			if (err.statusCode == 404) {
				this.scanPackageNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.PACKAGESCAN.NOTFOUND';
				this.data = [];
			} else if (err.data && err.data.errorMessage != null) {
				this.scanPackageNotification = err.data.errorMessage;
			} else {
				formInvalidate({ form: this.palletizingForm, err });
			}
			await this.soundService.error();
		}
		this.modalParams.onClose = this.modalParams.data.palletizeCallback;
	}

	@action.bound
	setRef(ref) {
		if (ref != null) {
			this.barcodeRef = ref;
		}
	}

	@action.bound
	async onUndo() {
		if (this.lastAction === 'palletize') {
			try {
				await this.shipmentInfoStore.undoPalletize(
					{ trackingNumber: this.trackingNumber, isFromLabelingMode: false },
					sourceOfChange.undoPalletizePalletizingMode
				);
				this.undoNotification = 'FULFILLMENTORDER.PALLETIZINGMODE.NOTIFICATION.UNDOSUCCESS';
				this.scanPackageNotification = null;
				this.lastAction = null;
				await this.shipmentInfoStore.find({
					page: 1,
					rpp: 100,
					palletId: this.palletizingForm.$('palletId').value,
					shipmentInfoType: shipmentInfoTypeEnum.shipment,
					embed: [
						'fulfillmentOrder',
						'fulfillmentOrder.fulfillmentOrderStatus',
						'fulfillmentOrder.fulfillmentOrderItems',
						'fulfillmentOrder.fulfillmentOrderItems.product',
					],
				});
				if (this.palletDropdownStore.value != null) {
					this.palletDropdownStore.value.extra.shipmentInfos = this.shipmentInfoStore.data;
				}
				this.data = [];
				this.transformData(this.shipmentInfoStore.data);
			} catch (err) {
				formInvalidate({ form: this.form, err });
			}
		}
	}

	@action.bound
	defineTableData() {
		this.tableData = {
			columns: [
				{
					key: 'fulfillmentOrder.fulfillmentOrderId',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.FULFILLMENTORDERID',
					dataCell: HyperlinkCellData,
					onClick: dataItem => this.openDetailPage(dataItem.id),
					selected: true,
					width: 80,
					filter: null,
				},
				{
					key: 'fulfillmentOrder.fulfillmentOrderStatus.name',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.STATUS',
					selected: true,
					width: 120,
					filter: null,
				},
				{
					key: 'fulfillmentOrder.product.sku',
					title: 'FULFILLMENTORDER.PALLETIZINGMODE.TABLEDATA.PRODUCTSKU',
					selected: true,
					width: 150,
					filter: null,
				},
			],
			actions: {
				onRemoveFromPallet: async item => this.openConfirmationModal(this.getRemoveFromPalletConfirmModalProps(item)),
			},
			sortObject: [
				{
					field: 'fulfillmentOrderId',
					dir: 'asc',
				},
			],
		};
		this.permissions = {
			onRemoveFromPallet: () => this.rootStore.authStore.hasPermission('fulfillmentOrderSection.full'),
		};
	}

	@action.bound
	transformData(shipmentInfos) {
		_.each(shipmentInfos, shipmentInfo => {
			if (shipmentInfo != null && shipmentInfo.fulfillmentOrder != null) {
				let product = {};
				if (shipmentInfo.fulfillmentOrder.fulfillmentOrderItems.length != 1) {
					product.name = 'Multiple';
					product.sku = 'Multiple';
				} else {
					product.name = shipmentInfo.fulfillmentOrder.fulfillmentOrderItems[0].product.name;
					product.sku = shipmentInfo.fulfillmentOrder.fulfillmentOrderItems[0].product.mainSku;
				}
				shipmentInfo.fulfillmentOrder.product = product;
				this.data.push(shipmentInfo);
			}
		});
	}

	@action.bound
	setInputs(input, field) {
		const key = field.key + 'Input';
		this[key] = input;
	}

	@action.bound
	onPrintLabel() {
		if (this.palletDropdownStore.value.extra.label != '') {
			this.label = createFileUrl(this.palletDropdownStore.value.extra.label);

			setTimeout(() => {
				const frame = window.frames['pdf-print-modal-pallet'];
				if (frame != null) {
					frame.print();
				}
			}, 250);
		}
	}

	openDetailPage = id => {
		this.rootStore.routerStore.goTo('master.app.main.order.list.detail', {
			id: id,
		});
	};

	getRemoveFromPalletConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.removeFromPallet(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['EXITSCANDETAILVIEW.MODAL.REMOVEFROMPALLET', { name: item.fulfillmentOrder.fulfillmentOrderId }],
			data: item,
		};
	};

	@action.bound async removeFromPallet(selectedItem) {
		this.suspend();
		try {
			this.data = _.filter(this.data, item => {
				return item.id != selectedItem.id;
			});
			const fulfillmentOrder = selectedItem.fulfillmentOrder;
			selectedItem.palletId = null;
			selectedItem.fulfillmentOrder = null;
			await this.shipmentInfoStore.updateShipmentInfo(
				selectedItem,
				true,
				sourceOfChange.palletizingModeRemoveFromPallet
			);
			this.rootStore.notificationStore.success([
				'EXITSCANDETAILVIEW.TABLE.ACTION.NOTIFICATION.REMOVEFROMPALLET',
				{ name: fulfillmentOrder.fulfillmentOrderId },
			]);
			await this.palletDropdownStore.updateFilter(this.palletFilter);
			this.palletDropdownStore.setValue(
				_.find(this.palletDropdownStore.options, palletOption => {
					return palletOption.value == this.palletDropdownStore.value.value;
				})
			);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	getDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.deletePallet(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['LOOKUPDELETECONFIRM.PALLET', { name: item.label }],
			data: item,
		};
	};

	@action.bound
	async deletePallet(pallet) {
		this.suspend();
		try {
			await this.palletStore.delete({ id: pallet.value });
			this.rootStore.notificationStore.success(['LOOKUPDELETE.PALLET', { name: pallet.label }]);
			this.palletDropdownStore.clear();
			this.palletFindNotification = null;
			await this.palletDropdownStore.reload();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}
}

export default PalletizingModeViewStore;



// WEBPACK FOOTER //
// ./src/modules/order/stores/PalletizingModeViewStore.js